import { useEffect, useState } from "react";
import { apiRequest } from "../../services/api";
export default function SettlementReversal({
  data,
  workspace,
  companyId,
  accessToken,
  run,
  busy,
  canWrite,
  requestKey,
  onSaved,
}) {
  const [preview, setPreview] = useState(null),
    [checks, setChecks] = useState({});
  useEffect(() => {
    setPreview(null);
    setChecks({});
  }, [data, workspace]);
  const url = `/accounting/${companyId}/settlement-evidence/postings`;
  function plan(event) {
    event.preventDefault();
    const { postingId, ...body } = Object.fromEntries(
      new FormData(event.currentTarget),
    );
    setPreview(null);
    setChecks({});
    run(
      () =>
        apiRequest(`${url}/${postingId}/reversal-preview`, {
          accessToken,
          method: "POST",
          body,
        }),
      setPreview,
    );
  }
  function reverse() {
    if (
      !canWrite ||
      busy ||
      !preview ||
      ![
        "bankCorrectionReviewed",
        "priorEntriesReviewed",
        "noTransferConfirmed",
      ].every((k) => checks[k] === true)
    )
      return;
    const body = {
      yearId: preview.payload.yearId,
      date: preview.payload.date,
      reason: preview.payload.reason,
      previewHash: preview.previewHash,
      ...checks,
    };
    body.requestKey = requestKey("settlement-reversal", body);
    run(
      async () => {
        await apiRequest(`${url}/${preview.payload.postingId}/reverse`, {
          accessToken,
          method: "POST",
          body,
        });
        return apiRequest(`/accounting/${companyId}/settlement-evidence`, {
          accessToken,
        });
      },
      (r) => {
        setPreview(null);
        setChecks({});
        onSaved?.(r);
      },
    );
  }
  const input = "block mt-1 rounded-lg border p-2 w-full";
  const posted = data.evidence.filter((e) => e.posting && !e.posting.reversal);
  return (
    <section className="mt-5 rounded-xl border border-amber-200 p-4">
      <h3 className="font-semibold">Correct a posted settlement</h3>
      <p className="my-2 text-sm">
        Reverse the accounting entry with retained history. This does not return
        money, execute a refund or create a provider chargeback. Then explicitly
        release old allocations and supersede the evidence before posting a
        corrected replacement.
      </p>
      <form
        onSubmit={plan}
        onChange={() => {
          setPreview(null);
          setChecks({});
        }}
        className="grid gap-3 md:grid-cols-2"
      >
        <label>
          Original settlement
          <select required name="postingId" className={input} defaultValue="">
            <option value="">Select unreversed posting</option>
            {posted.map((e) => (
              <option key={e.id} value={e.posting.id}>
                {e.bankCreditReference} · {e.posting.voucher?.number}
              </option>
            ))}
          </select>
        </label>
        <label>
          Reversal financial year
          <select required name="yearId" className={input} defaultValue="">
            <option value="">Select year</option>
            {workspace.years.map((y) => (
              <option key={y.id} value={y.id}>
                {y.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Reversal date
          <input name="date" type="date" required className={input} />
        </label>
        <label>
          Correction reason
          <textarea
            name="reason"
            minLength={20}
            maxLength={1000}
            required
            className={input}
          />
        </label>
        <button
          disabled={busy || !posted.length}
          className="rounded-lg border p-2"
        >
          Preview settlement reversal
        </button>
      </form>
      {preview && (
        <div className="mt-3 space-y-3">
          <p className="text-sm">{preview.warning}</p>
          <table className="w-full text-left text-sm">
            <thead>
              <tr>
                <th>Original ledger</th>
                <th>Debit</th>
                <th>Credit</th>
              </tr>
            </thead>
            <tbody>
              {preview.payload.lines.map((l) => (
                <tr key={l.ledgerId}>
                  <td>
                    {workspace.ledgers.find((x) => x.id === l.ledgerId)?.name ||
                      l.ledgerId}
                  </td>
                  <td>{l.debit}</td>
                  <td>{l.credit}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {canWrite && (
            <>
              {[
                [
                  "bankCorrectionReviewed",
                  "I reviewed why the original bank-settlement accounting is incorrect.",
                ],
                [
                  "priorEntriesReviewed",
                  "I checked the original entry and any prior corrections.",
                ],
                [
                  "noTransferConfirmed",
                  "I understand this is an accounting reversal only, not a bank transfer or refund.",
                ],
              ].map(([key, label]) => (
                <label className="block text-sm" key={key}>
                  <input
                    type="checkbox"
                    checked={!!checks[key]}
                    onChange={(e) =>
                      setChecks({ ...checks, [key]: e.target.checked })
                    }
                  />{" "}
                  {label}
                </label>
              ))}
              <button
                type="button"
                disabled={
                  busy || Object.values(checks).filter(Boolean).length !== 3
                }
                className="rounded-lg border border-red-300 p-2 disabled:opacity-40"
                onClick={reverse}
              >
                Record reviewed settlement reversal
              </button>
            </>
          )}
        </div>
      )}
      <ul className="mt-3 text-sm">
        {data.evidence
          .filter((e) => e.posting?.reversal)
          .map((e) => (
            <li key={e.id}>
              {e.bankCreditReference}: original {e.posting.voucher?.number}{" "}
              reversed by {e.posting.reversal.voucher?.number} ·{" "}
              {e.posting.reversal.reason}
            </li>
          ))}
      </ul>
    </section>
  );
}
